import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import {
  BrowserNtfDisplayedRequest,
  BrowserNtfEventType,
  BrowserNtfHistoryRequest,
  BrowserNtfObj
} from 'src/lib/fliko/models/browserNtf/browserNtf.data';
import { ContextService } from 'src/app/view/user-context/services/context.service';
import { ErrorHandlingService } from '../../error-handling/error-handling.service';
import { FunctionsApiService } from '../functions-api.service';
import { SentryService } from '../sentry.service';
import { BrowserNtfSwMessageData } from './browser-ntf.models';
import { BrowserNtfPushRuntimeService } from './browser-ntf-push-runtime.service';

interface BrowserNtfHistoryState {
  eventTypes: BrowserNtfEventType[] | null;
  notificationsSubject: BehaviorSubject<BrowserNtfObj[]>;
  loadingSubject: BehaviorSubject<boolean>;
  hasMoreSubject: BehaviorSubject<boolean>;
  loadedOnce: boolean;
}

@Injectable({ providedIn: 'root' })
export class BrowserNtfHistoryService implements OnDestroy {
  private readonly pageLimit = 30;
  private readonly maxNotifications = 100;
  private readonly pushRefreshDelayMs = 1500;
  private readonly subscription$ = new Subscription();

  private readonly notificationsSubject = new BehaviorSubject<BrowserNtfObj[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly hasMoreSubject = new BehaviorSubject<boolean>(false);
  private readonly globalState: BrowserNtfHistoryState = {
    eventTypes: null,
    notificationsSubject: this.notificationsSubject,
    loadingSubject: this.loadingSubject,
    hasMoreSubject: this.hasMoreSubject,
    loadedOnce: false
  };
  private readonly scopedStates = new Map<string, BrowserNtfHistoryState>();

  public readonly notifications$: Observable<BrowserNtfObj[]> = this.notificationsSubject.asObservable();
  public readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();
  public readonly hasMore$: Observable<boolean> = this.hasMoreSubject.asObservable();
  public readonly unreadCount$: Observable<number> = this.notifications$.pipe(
    map((notifications) => notifications.filter((notification) => !notification.displayed).length)
  );

  public notificationsFor$(eventTypes: readonly BrowserNtfEventType[]): Observable<BrowserNtfObj[]> {
    return this.getState(eventTypes).notificationsSubject.asObservable();
  }

  public unreadCountFor$(eventTypes: readonly BrowserNtfEventType[]): Observable<number> {
    return this.notificationsFor$(eventTypes).pipe(
      map((notifications) => notifications.filter((notification) => !notification.displayed).length)
    );
  }

  public loadingFor$(eventTypes: readonly BrowserNtfEventType[]): Observable<boolean> {
    return this.getState(eventTypes).loadingSubject.asObservable();
  }

  public hasMoreFor$(eventTypes: readonly BrowserNtfEventType[]): Observable<boolean> {
    return this.getState(eventTypes).hasMoreSubject.asObservable();
  }

  private currentUserId: string | null = null;
  private pushRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private functionsApi: FunctionsApiService,
    private errorHandlingService: ErrorHandlingService,
    private sentryService: SentryService,
    private pushRuntimeService: BrowserNtfPushRuntimeService,
    private ctxSrv: ContextService
  ) {
    this.subscription$.add(this.ctxSrv.getUserId$().subscribe((userId) => this.handleUserChange(userId)));
    this.subscription$.add(
      this.pushRuntimeService.pushReceived$.subscribe((payload) => this.handleIncomingPush(payload))
    );
    this.subscription$.add(
      this.pushRuntimeService.notificationOpened$.subscribe((notificationId) => {
        void this.setDisplayedByIds([notificationId], true);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
    if (this.pushRefreshTimer) clearTimeout(this.pushRefreshTimer);
  }

  public async loadInitial(eventTypes?: readonly BrowserNtfEventType[]): Promise<void> {
    const state = this.getState(eventTypes);
    if (state.loadedOnce || state.loadingSubject.value) return;
    await this.loadPage(state, false);
  }

  public async refresh(eventTypes?: readonly BrowserNtfEventType[]): Promise<void> {
    const state = this.getState(eventTypes);
    if (state.loadingSubject.value) return;
    await this.loadPage(state, false);
  }

  public async loadMore(eventTypes?: readonly BrowserNtfEventType[]): Promise<void> {
    const state = this.getState(eventTypes);
    if (state.loadingSubject.value || !state.hasMoreSubject.value) return;

    const notifications = state.notificationsSubject.value;
    if (notifications.length >= this.maxNotifications) {
      state.hasMoreSubject.next(false);
      return;
    }

    const lastNotification = notifications[notifications.length - 1];
    if (!lastNotification) return;

    await this.loadPage(state, true, lastNotification.createDate);
  }

  public async setDisplayed(notification: BrowserNtfObj, displayed: boolean): Promise<void> {
    await this.setDisplayedByIds([notification.id], displayed);
  }

  public async setDisplayedByIds(ids: string[], displayed: boolean): Promise<void> {
    if (!ids.length) return;

    const previous = this.snapshotStates();
    this.patchLocalDisplayed(new Set(ids), displayed);

    try {
      await this.functionsApi.callFunctionOrThrow<BrowserNtfDisplayedRequest, boolean>('setBrowserNtfDisplayed', {
        ids,
        displayed
      });
    } catch (error) {
      this.restoreStates(previous);
      this.errorHandlingService.showGlobalError('error.browserNtfUpdate', error);
    }
  }

  public async setAllDisplayed(
    displayed: boolean,
    eventTypes?: readonly BrowserNtfEventType[]
  ): Promise<void> {
    const previous = this.snapshotStates();
    const types = eventTypes ? new Set(eventTypes) : null;
    this.allStates().forEach((state) =>
      state.notificationsSubject.next(
        state.notificationsSubject.value.map((notification) =>
          !types || types.has(notification.eventType as BrowserNtfEventType)
            ? { ...notification, displayed }
            : notification
        )
      )
    );

    try {
      await this.functionsApi.callFunctionOrThrow<BrowserNtfDisplayedRequest, boolean>('setBrowserNtfDisplayed', {
        all: true,
        ids: this.globalState.notificationsSubject.value.map((notification) => notification.id),
        displayed,
        ...(eventTypes ? { eventTypes: [...eventTypes] } : {})
      });
    } catch (error) {
      this.restoreStates(previous);
      this.errorHandlingService.showGlobalError('error.browserNtfUpdate', error);
    }
  }

  private async refreshAfterPush(): Promise<void> {
    const states = this.allStates().filter((state) => state.loadedOnce && !state.loadingSubject.value);
    await Promise.all(
      states.map((state) => this.loadPage(state, false, undefined, { mergeWithCurrent: true, showError: false }))
    );
  }

  private async loadPage(
    state: BrowserNtfHistoryState,
    append: boolean,
    beforeCreateDate?: number,
    options: { mergeWithCurrent?: boolean; showError?: boolean } = {}
  ): Promise<void> {
    const { mergeWithCurrent = false, showError = true } = options;
    const userId = this.currentUserId;
    if (!userId) return;

    const pageLimit = append
      ? Math.min(this.pageLimit, this.maxNotifications - state.notificationsSubject.value.length)
      : this.pageLimit;
    if (pageLimit <= 0) {
      state.hasMoreSubject.next(false);
      return;
    }

    state.loadingSubject.next(true);

    try {
      const page = await this.fetchHistoryPage(beforeCreateDate, pageLimit, state.eventTypes);
      if (this.currentUserId !== userId) return;

      const pageNotifications = Array.isArray(page)
        ? page.map((notification) => ({ ...notification, displayed: notification.displayed ?? false }))
        : [];

      const shouldMergeCurrent = append || mergeWithCurrent;
      const merged = shouldMergeCurrent
        ? this.mergeNotifications(state.notificationsSubject.value, pageNotifications)
        : pageNotifications;
      const sortedNotifications = this.sortNotifications(merged);
      state.notificationsSubject.next(sortedNotifications);
      state.hasMoreSubject.next(pageNotifications.length === pageLimit && sortedNotifications.length < this.maxNotifications);
      state.loadedOnce = true;
    } catch (error) {
      if (showError) {
        this.errorHandlingService.showGlobalError('error.browserNtfHistory', error);
      } else {
        this.sentryService.captureError(error);
      }
    } finally {
      if (this.currentUserId === userId) state.loadingSubject.next(false);
    }
  }

  private handleUserChange(userId: string | null): void {
    if (this.currentUserId === userId) return;

    this.currentUserId = userId;
    this.allStates().forEach((state) => {
      state.loadedOnce = false;
      state.notificationsSubject.next([]);
      state.hasMoreSubject.next(false);
      state.loadingSubject.next(false);
    });

    if (this.pushRefreshTimer) {
      clearTimeout(this.pushRefreshTimer);
      this.pushRefreshTimer = null;
    }

    if (userId) void this.loadInitial();
  }

  private handleIncomingPush(payload: BrowserNtfSwMessageData): void {
    this.mergeIncomingPush(payload);
    this.schedulePushRefresh();
  }

  private mergeIncomingPush(payload: BrowserNtfSwMessageData): void {
    const id = payload.notificationId;
    if (!id || !this.currentUserId) return;

    const createDate = payload.createDate ?? Date.now();
    const notification: BrowserNtfObj = {
      id,
      userId: this.currentUserId,
      createDate,
      lastUpdate: createDate,
      displayed: false,
      title: payload.title,
      body: payload.body,
      eventType: payload.eventType ?? undefined,
      deepLink: payload.url ?? '',
      estateId: payload.estateId ?? null,
      estateName: payload.estateName ?? null
    };

    this.allStates().forEach((state) => {
      if (
        state.eventTypes &&
        (!notification.eventType || !state.eventTypes.includes(notification.eventType as BrowserNtfEventType))
      ) {
        return;
      }
      if (state.notificationsSubject.value.some((value) => value.id === id)) return;
      state.notificationsSubject.next(this.sortNotifications([notification, ...state.notificationsSubject.value]));
    });
  }

  private schedulePushRefresh(): void {
    if (this.pushRefreshTimer) clearTimeout(this.pushRefreshTimer);

    this.pushRefreshTimer = setTimeout(() => {
      this.pushRefreshTimer = null;
      void this.refreshAfterPush();
    }, this.pushRefreshDelayMs);
  }

  private fetchHistoryPage(
    beforeCreateDate: number | undefined,
    limit: number,
    eventTypes: BrowserNtfEventType[] | null
  ): Promise<BrowserNtfObj[]> {
    const request: BrowserNtfHistoryRequest = { limit };
    if (beforeCreateDate) request.beforeCreateDate = beforeCreateDate;
    if (eventTypes) request.eventTypes = eventTypes;

    return this.functionsApi.callFunctionOrThrow<BrowserNtfHistoryRequest, BrowserNtfObj[]>(
      'getBrowserNtfHistory',
      request
    );
  }

  private patchLocalDisplayed(ids: Set<string>, displayed: boolean): void {
    this.allStates().forEach((state) =>
      state.notificationsSubject.next(
        state.notificationsSubject.value.map((notification) =>
          ids.has(notification.id) ? { ...notification, displayed } : notification
        )
      )
    );
  }

  private getState(eventTypes?: readonly BrowserNtfEventType[]): BrowserNtfHistoryState {
    const normalizedEventTypes = eventTypes ? [...new Set(eventTypes)].sort() : [];
    if (!normalizedEventTypes.length) return this.globalState;

    const key = normalizedEventTypes.join('|');
    let state = this.scopedStates.get(key);
    if (!state) {
      state = {
        eventTypes: normalizedEventTypes,
        notificationsSubject: new BehaviorSubject<BrowserNtfObj[]>([]),
        loadingSubject: new BehaviorSubject<boolean>(false),
        hasMoreSubject: new BehaviorSubject<boolean>(false),
        loadedOnce: false
      };
      this.scopedStates.set(key, state);
    }
    return state;
  }

  private allStates(): BrowserNtfHistoryState[] {
    return [this.globalState, ...this.scopedStates.values()];
  }

  private snapshotStates(): Map<BrowserNtfHistoryState, BrowserNtfObj[]> {
    return new Map(this.allStates().map((state) => [state, state.notificationsSubject.value]));
  }

  private restoreStates(snapshot: Map<BrowserNtfHistoryState, BrowserNtfObj[]>): void {
    snapshot.forEach((notifications, state) => state.notificationsSubject.next(notifications));
  }

  private mergeNotifications(current: BrowserNtfObj[], incoming: BrowserNtfObj[]): BrowserNtfObj[] {
    const map = MapHlp.idMap(current);
    incoming.forEach((notification) => map.set(notification.id, notification));
    return [...map.values()];
  }

  private sortNotifications(notifications: BrowserNtfObj[]): BrowserNtfObj[] {
    return [...notifications]
      .sort((a, b) => b.createDate - a.createDate || b.id.localeCompare(a.id))
      .slice(0, this.maxNotifications);
  }
}
